// @ts-nocheck
import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import {
    CurrentLocationInfoWindow,
    LocationGoogleMapMarkerType,
    LocationGoogleMapsMarker,
    LocationInfoWindow,
} from "@insite/client-framework/Common/Hooks/useLocationGoogleMarkers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import withDynamicGoogleMaps from "@insite/client-framework/Common/withDynamicGoogleMaps";
import translate from "@insite/client-framework/Translate";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import LocationContentLink from "@insite/content-library/Components/LocationContentLink";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { GoogleMapProps, InfoWindowProps, MarkerProps } from "@react-google-maps/api";
import * as React from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { locationProps } from "@store/Themes/common/props/location";
import useGoogleMapThemableMarkers from "@toolkit/Framework/Common/Hooks/googleMap/useGoogleMapThemableMarkers";
import useGoogleMapThemableProps from "@toolkit/Framework/Common/Hooks/googleMap/useGoogleMapThemableProps";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
/* Starter Custom Code - End */

interface Props {
    extendedStyles?: LocationGoogleMapStyles;
    currentLocation?: google.maps.LatLng;
    locationSearchFilter: string;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    mapMarkerElements: LocationGoogleMapsMarker[];
    locationInfoWindow?: LocationInfoWindow<LocationModel>;
    currentLocationInfoWindow?: CurrentLocationInfoWindow;
    handleOpenLocationContent: (location: LocationModel) => void;
    setGoogleMap: (map: google.maps.Map | undefined) => void;
    GoogleMap: React.ComponentType<GoogleMapProps>;
    InfoWindow: React.ComponentType<InfoWindowProps>;
    Marker: React.ComponentType<MarkerProps>;
}

export interface LocationGoogleMapStyles {
    googleMap?: GoogleMapProps;
    locationMarker?: MarkerProps;
    currentLocationMarker?: MarkerProps;
    selectedMarker?: MarkerProps;
    locationInfoWindow?: InfoWindowProps;
    currentLocationInfoWindow?: InfoWindowProps;
    infoWindowGridContainer?: GridContainerProps;
    infoWindowLeftGridItem?: GridItemProps;
    selectedLocationName?: TypographyPresentationProps;
    selectedLocationAddressDisplay?: AddressInfoCondensedDisplayStyles;
    infoWindowRightGridItem?: GridItemProps;
    infoWindowLocationContentLink?: LinkPresentationProps;
    infoWindowGoogleMapsDirectionLink?: LinkPresentationProps;
    infoWindowDistanceDisplay?: TypographyPresentationProps;
    currentLocationInfoWindowGridContainer?: GridContainerProps;
    currentLocationInfoWindowHeaderGridItem?: GridItemProps;
    currentLocationInfoWindowHeaderText?: TypographyPresentationProps;
    currentLocationInfoWindowSearchFilterGridItem?: GridItemProps;
    currentLocationInfoWindowSearchFilterText?: TypographyPresentationProps;
    // Starter Custom Code
    infoWindowWrapper?: InjectableCss;
    infoWindowTitleWrapper?: InjectableCss;
}

export const locationGoogleMapStyles: LocationGoogleMapStyles = {
    infoWindowWrapper: locationProps.infoWindow?.wrapper,
    infoWindowTitleWrapper: locationProps.infoWindow?.titleWrapper,
    infoWindowDistanceDisplay: locationProps.infoWindow?.distanceText,
    selectedLocationName: locationProps.infoWindow?.title,
    selectedLocationAddressDisplay: locationProps.infoWindow?.addressInfo,
    currentLocationInfoWindowHeaderText: locationProps.infoWindow?.title,
    infoWindowLocationContentLink: locationProps.infoWindow?.actionLink,
    infoWindowGoogleMapsDirectionLink: locationProps.infoWindow?.actionLink,
};

const LocationGoogleMap: React.FC<Props> = ({
    extendedStyles,
    currentLocation,
    locationSearchFilter,
    distanceUnitOfMeasure,
    setGoogleMap,
    mapMarkerElements: baseMapMarkerElements, // Starter Custom Code
    locationInfoWindow,
    currentLocationInfoWindow,
    handleOpenLocationContent,
    GoogleMap,
    InfoWindow,
    Marker,
}) => {
    const styles = useMergeProps(locationGoogleMapStyles, extendedStyles); // Starter Custom Code
    const googleMapProps = useGoogleMapThemableProps(styles.googleMap); // Starter Custom Code
    const mapMarkerElements = useGoogleMapThemableMarkers(baseMapMarkerElements); // Starter Custom Code

    const handleLoad = (map: google.maps.Map) => {
        setGoogleMap(map);
    };
    const handleUnmount = (_: google.maps.Map) => {
        setGoogleMap(undefined);
    };

    const getMarkerStyles = (markerType: LocationGoogleMapMarkerType) => {
        if (markerType === "CURRENT_LOCATION") {
            return styles.currentLocationMarker;
        }
        if (markerType === "SELECTED") {
            return styles.selectedMarker;
        }
        return styles.locationMarker;
    };

    return (
        <GoogleMap
            {...googleMapProps} // Starter Custom Code
            center={currentLocation}
            onLoad={handleLoad}
            onUnmount={handleUnmount}
        >
            {mapMarkerElements.map(marker => (
                <Marker {...marker} key={marker.key} {...getMarkerStyles(marker.type)} />
            ))}
            {locationInfoWindow && (
                <InfoWindow {...styles.locationInfoWindow} key={newGuid()} position={locationInfoWindow?.position}>
                    {/* Starter Custom Code - Start */}
                    <StyledWrapper {...styles.infoWindowWrapper}>
                        <StyledWrapper {...styles.infoWindowTitleWrapper}>
                            <Typography {...styles.selectedLocationName}>{locationInfoWindow.location.name}</Typography>
                            {locationInfoWindow.location.distance > 0.01 && (
                                <DistanceDisplay
                                    {...styles.infoWindowDistanceDisplay}
                                    distance={locationInfoWindow.location.distance}
                                    unitOfMeasure={distanceUnitOfMeasure}
                                />
                            )}
                        </StyledWrapper>
                        <AddressInfoCondensedDisplay
                            {...locationInfoWindow.location}
                            extendedStyles={styles.selectedLocationAddressDisplay}
                        />
                        {locationInfoWindow.location.htmlContent && (
                            <LocationContentLink
                                {...styles.infoWindowLocationContentLink}
                                location={locationInfoWindow.location}
                                onOpenLocationContent={handleOpenLocationContent}
                            />
                        )}
                        <GoogleMapsDirectionLink
                            {...styles.infoWindowGoogleMapsDirectionLink}
                            {...locationInfoWindow.location}
                        />
                    </StyledWrapper>
                    {/* Starter Custom Code - End */}
                </InfoWindow>
            )}
            {currentLocationInfoWindow && (
                <InfoWindow
                    {...styles.currentLocationInfoWindow}
                    key={newGuid()}
                    position={currentLocationInfoWindow.position}
                >
                    {/* Starter Custom Code - Start */}
                    <StyledWrapper {...styles.infoWindowWrapper}>
                        <StyledWrapper {...styles.infoWindowTitleWrapper}>
                            <Typography {...styles.currentLocationInfoWindowHeaderText}>
                                {translate("Current Location")}
                            </Typography>
                        </StyledWrapper>
                        {locationSearchFilter ? (
                            <Typography {...styles.currentLocationInfoWindowSearchFilterText}>
                                {locationSearchFilter}
                            </Typography>
                        ) : null}
                    </StyledWrapper>
                    {/* Starter Custom Code - End */}
                </InfoWindow>
            )}
        </GoogleMap>
    );
};

export default withDynamicGoogleMaps(LocationGoogleMap);
